import type { Root } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import { openWeeklyReview, type WeeklyReviewAction } from "./weekly-review-controller.ts";
import { advanceWeeklyReview, type WeeklyReviewState } from "./weekly-review.ts";

export interface WeeklyReviewWorkflowCapabilities {
  state(): WeeklyReviewState | null;
  setState(state: WeeklyReviewState | null): void;
  tasks(): Todo[];
  persist(state: WeeklyReviewState | null): Promise<void>;
  ensureTasks(): Promise<void>;
  categories(): Promise<Category[]>;
  roots(): Root[];
  date(): string;
  addDays(date: string, days: number): string;
  canEdit(task: Todo): boolean;
  blockers(task: Todo): Todo[];
  performTaskAction(task: Todo, action: WeeklyReviewAction, value?: string): Promise<boolean>;
  openTask(task: Todo): Promise<void>;
  openSettings(): void;
  openDailyNotes?(): Promise<void>;
  icon(name: string): string;
  render(): Promise<void>;
  sync(): void | Promise<void>;
  now(): number;
}

export function weeklyReviewRemaining(state: WeeklyReviewState | null, tasks: Todo[]): number {
  if (!state || state.completedAt) return 0;
  const available = new Set(tasks.filter(task => !task.deleted && !task.archivedAt).map(task => task.id));
  return state.taskIds.filter(id => !state.reviewedIds.includes(id) && available.has(id)).length;
}

export function createWeeklyReviewWorkflowController(capabilities: WeeklyReviewWorkflowCapabilities) {
  const save = async (state: WeeklyReviewState | null): Promise<void> => { await capabilities.persist(state); capabilities.setState(state); };
  const perform = async (task: Todo, action: WeeklyReviewAction, value?: string): Promise<boolean> => {
    if (action !== "keep" && action !== "defer" && (!capabilities.canEdit(task) || !await capabilities.performTaskAction(task, action, value))) return false;
    const state = capabilities.state(); if (state) await save(advanceWeeklyReview(state, task.id, capabilities.now(), action));
    return true;
  };
  return {
    remaining: () => weeklyReviewRemaining(capabilities.state(), capabilities.tasks()),
    save,
    perform,
    open: (restart = false) => openWeeklyReview({ tasks: capabilities.tasks, state: capabilities.state, saveState: state => save(state), ensureTasks: capabilities.ensureTasks, categories: capabilities.categories, roots: capabilities.roots, date: capabilities.date, addDays: capabilities.addDays, canEdit: capabilities.canEdit, blockers: capabilities.blockers, perform, openTask: capabilities.openTask, openSettings: capabilities.openSettings, openDailyNotes: capabilities.openDailyNotes, icon: capabilities.icon, renderShell: capabilities.render, sync: capabilities.sync }, restart),
  };
}
